import { Box, Typography, Stack, Grid, Avatar } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Badge } from '../../../../shared/components/ui/Badge';
import { Card } from '../../../../shared/components/ui/Card';
import type { Tenant } from '../../context/TenantContext';

interface TenantInfoProps {
  tenant: Tenant;
}

export function TenantInfo({ tenant }: TenantInfoProps) {
  return (
    <>
      {/* Perfil */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 4 }}>
        <Box sx={{ position: 'relative', width: 110, height: 110 }}>
          <Avatar sx={{ width: 110, height: 110, fontSize: 48, bgcolor: 'primary.main' }}>
            {tenant.fantasyName?.[0] || tenant.name?.[0]}
          </Avatar>
        </Box>
        <Box flex={1}>
          <Typography sx={{ color: '#23272E', fontWeight: 800, fontSize: 28, letterSpacing: -1 }}>
            {tenant.fantasyName || tenant.name} <Badge color={tenant.status === 'active' ? 'success' : 'default'}>{tenant.status}</Badge>
          </Typography>
          <Typography sx={{ color: '#6B7280', fontSize: 16, fontWeight: 500 }}>CNPJ: {tenant.documentNumber}</Typography>
          <Stack direction="row" spacing={2} mt={1} alignItems="center">
            {tenant.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmailIcon sx={{ color: '#6B7280', fontSize: 16 }} />
                <Typography sx={{ color: '#6B7280', fontSize: 14 }}>{tenant.email}</Typography>
              </Box>
            )}
            {(tenant.phoneCode && tenant.phoneNumber) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneIcon sx={{ color: '#6B7280', fontSize: 16 }} />
                <Typography sx={{ color: '#6B7280', fontSize: 14 }}>({tenant.phoneCode}) {tenant.phoneNumber}</Typography>
              </Box>
            )}
            {tenant.site && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LanguageIcon sx={{ color: '#6B7280', fontSize: 16 }} />
                <Typography sx={{ color: '#6B7280', fontSize: 14 }}>{tenant.site}</Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Detalhes e Endereço */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ background: '#F9FAFB', borderRadius: 3, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BusinessIcon sx={{ color: '#3B4EFF', fontSize: 22, mr: 1 }} />
              <Typography fontWeight={700} fontSize={17} color="#23272E">Dados da Empresa</Typography>
            </Box>
            <Box component="dl" sx={{ m: 0 }}>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Razão Social:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{tenant.corporateName}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Nome Fantasia:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{tenant.fantasyName}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Segmento:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{tenant.type.name}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>CNPJ:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{tenant.documentNumber}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>IE:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{tenant.stateRegistration}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>IM:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{tenant.municipalRegistration}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Status:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>
                  <Badge color={tenant.status === 'active' ? 'success' : 'default'}>{tenant.status}</Badge>
                </dd>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ background: '#F9FAFB', borderRadius: 3, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationOnIcon sx={{ color: '#3B4EFF', fontSize: 22, mr: 1 }} />
              <Typography fontWeight={700} fontSize={17} color="#23272E">Endereço</Typography>
            </Box>
            <Box component="dl" sx={{ m: 0 }}>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Endereço:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>
                  {tenant.street}, {tenant.streetNumber} {tenant.complement && `- ${tenant.complement}`}
                </dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Bairro:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{tenant.district}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>CEP:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{tenant.zipCode}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Cidade/UF:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>Cidade / Estado</dd>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}